import React, { useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Alert, AlertTitle, AlertDescription, AlertActions } from '../../../../catalyst-components/alert';
import { getDateTIme } from '../../../../shared/helpers/get-date-time';
import Markdown from 'react-markdown';

export const PrivacyPolicyTab: React.FC = () => {
  const [content, setContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const latestPolicy = useQuery(api.privacy_policy.getLatest);
  const policies = useQuery(api.privacy_policy.getAllPolicies);

  const createPolicy = useMutation(api.privacy_policy.create);


  useEffect(() => {
    if (latestPolicy) {
      setContent(latestPolicy.content);
    }
  }, [latestPolicy]);

  const handleSave = async () => {
    try {
      const latestVersion = latestPolicy?.version ?? 0;
      await createPolicy({ 
        content,
        version: latestVersion + 1
      });
      setAlertMessage('Privacy Policy saved successfully!');
      setSuccess(true);
      setShowAlert(true);
    } catch (error) {
      setAlertMessage('Error saving Privacy Policy. Please try again.');
      setSuccess(false);
      setShowAlert(true);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 mb-8">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Create New Version</h2>
          <Button 
            color="light" 
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
        </div>

        {showPreview ? (
          <div className="prose max-w-none p-4 bg-primary-1 rounded-lg border">
            <Markdown>{content}</Markdown>
          </div>
        ) : (
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter privacy policy text (supports Markdown)"
            rows={10}
          />
        )}

        <div className="flex justify-end">
          <Button 
            color="dark" 
            onClick={handleSave}
            disabled={!content.trim()}
          >
            Save New Version
          </Button>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-4">Version History</h2>
      <Table striped>
        <TableHead>
          <TableRow>
            <TableHeader>Version</TableHeader>
            <TableHeader>Created At</TableHeader>
            <TableHeader>Created By</TableHeader>
            <TableHeader>Content Preview</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {policies?.map((policy) => (
              <TableRow key={policy._id}>
              <TableCell>{policy.version}</TableCell>
              <TableCell>{getDateTIme(policy.created_at)}</TableCell>
              <TableCell>{policy.creator_email}</TableCell>
              <TableCell className="max-w-xl truncate">{policy.content}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
        <AlertTitle>{success ? 'Success' : 'Error'}</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setShowAlert(false)}>
            Ok
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
